// Transactions.jsx - Admin Bank Perspective View (Filters Responsive + Original Table Design)

import React, { useEffect, useState, useMemo } from "react";
import {
  Table, Tag, Input, Select, DatePicker, message, Tabs,
  Button, Dropdown, Space, Tooltip, Drawer, Divider,
} from "antd";
import {
  SearchOutlined, DollarOutlined, EyeOutlined, ReloadOutlined,
  FileExcelOutlined, FilePdfOutlined, DownOutlined, UpOutlined,
  RiseOutlined, FallOutlined, ClockCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, TransactionOutlined,
  UserOutlined, CalendarOutlined, CreditCardOutlined, IdcardOutlined,
  BankOutlined, FileTextOutlined, CrownOutlined, GiftOutlined,
} from "@ant-design/icons";
import {
  UserTransactionsGet,
  StaffTransactionsGet,
  DevTransactionsGet,
} from "../../../../Api/CoreApi";
import { exportToCSV } from "../../Utils/exportCSV";
import { exportToPDF } from "../../Utils/exportPDF";
import {
  formatDate, formatDateTime,
  generateRefNumber, capitalize,
} from "../../Utils/formatters";
import "./Transactions.css";

const { Option } = Select;
const { RangePicker } = DatePicker;

function Transactions() {
  const [userTransactions, setUserTransactions] = useState([]);
  const [staffTransactions, setStaffTransactions] = useState([]);
  const [devTransactions, setDevTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 📱 Mobile View States
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const userRole = localStorage.getItem("role") || "admin";
  const isDeveloper = userRole === "developer";
  const isAdmin = userRole === "admin" || userRole === "superadmin";

  // ============================================
  // HELPER FUNCTIONS (ORIGINAL)
  // ============================================
  const getTxUserName = (r) => r.user_name || r.username || r.staff_username || r.dev_username || "N/A";
  const getTxContact = (r) => r.contact || r.upi_id || "N/A";
  const getTxDate = (r) => r.created_date || r.date || null;

  const isAdminGrant = (tx) => tx.ref_id && String(tx.ref_id).startsWith('ADMIN-GRANT-');
  const isStaffGrant = (tx) => {
    if (!tx.description) return false;
    return tx.description.includes('(Staff)') || tx.description.includes('Staff:');
  };
  const isAdminGrantSource = (tx) => {
    if (!tx.description) return false;
    return tx.description.includes('(Admin)') || tx.description.includes('Admin:');
  };

  const getGrantSource = (tx) => {
    if (isStaffGrant(tx)) return 'Staff';
    if (isAdminGrantSource(tx)) return 'Admin';
    return 'Admin';
  };

  const getPaymentModeFromUTI = (uti) => {
    const utiStr = String(uti || '').toUpperCase();
    if (utiStr.startsWith('CASH-')) return { label: 'CASH', icon: '💵', color: 'green', full: 'Cash Payment' };
    if (utiStr.startsWith('GIFT-')) return { label: 'FREE', icon: '🎁', color: 'pink', full: 'Free / Complimentary' };
    if (utiStr.startsWith('ONLINE-')) return { label: 'ONLINE', icon: '💳', color: 'blue', full: 'Online Payment' };
    if (utiStr.startsWith('UPI-')) return { label: 'UPI', icon: '📱', color: 'purple', full: 'UPI Transfer' };
    if (utiStr.startsWith('BANK-')) return { label: 'BANK', icon: '🏦', color: 'cyan', full: 'Bank Transfer' };
    return null;
  };

  const getAdminType = (tx) => {
    if (tx._source === 'user') {
      if (tx.plan_name === 'User Referral Bonus' ||
        (tx.ref_id && String(tx.ref_id).startsWith('USER-REFER-'))) {
        return 'debit';
      }
      return 'credit';
    }
    if (tx._source === 'staff') return 'debit';
    if (tx._source === 'dev') return 'debit';
    return tx.type;
  };

  const getAdminCategory = (tx) => {
    if (isAdminGrant(tx)) {
      const uti = String(tx.UTI || '').toUpperCase();
      const isStaff = isStaffGrant(tx);
      const grantLabel = isStaff ? 'Staff Grant' : 'Admin Grant';

      if (uti.startsWith('CASH-')) return { label: `Cash Payment (${grantLabel})`, icon: '💵', color: '#059669', bg: '#d1fae5', border: '#6ee7b7' };
      if (uti.startsWith('GIFT-')) return { label: `Free Gift (${grantLabel})`, icon: '🎁', color: '#ec4899', bg: '#fce7f3', border: '#f9a8d4' };
      if (uti.startsWith('ONLINE-')) return { label: `Online ${grantLabel}`, icon: '💳', color: '#3b82f6', bg: '#dbeafe', border: '#93c5fd' };
      if (uti.startsWith('UPI-')) return { label: `UPI ${grantLabel}`, icon: '📱', color: '#8b5cf6', bg: '#ede9fe', border: '#c4b5fd' };
      if (uti.startsWith('BANK-')) return { label: `Bank Transfer ${grantLabel}`, icon: '🏦', color: '#0891b2', bg: '#cffafe', border: '#67e8f9' };
      return {
        label: isStaff ? 'Staff Premium Grant' : 'Admin Premium Grant',
        icon: isStaff ? '👥' : '👑', color: isStaff ? '#059669' : '#7c3aed',
        bg: isStaff ? '#d1fae5' : '#ede9fe', border: isStaff ? '#6ee7b7' : '#c4b5fd'
      };
    }
    if (tx.plan_name === 'User Referral Bonus' || (tx.ref_id && String(tx.ref_id).startsWith('USER-REFER-'))) {
      return { label: 'Referral Bonus', icon: '🎁', color: '#f59e0b', bg: '#fef3c7', border: '#fde68a' };
    }
    if (tx.plan_name === 'Profile Visit Commission' || (tx.ref_id && String(tx.ref_id).startsWith('VISIT-')) || (tx.description && tx.description.toLowerCase().includes('visit'))) {
      return { label: 'Visit Commission', icon: '👀', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' };
    }
    if (tx._source === 'staff' && tx.type === 'credit') return { label: 'Payment to Staff', icon: '👥', color: '#2563eb', bg: '#dbeafe', border: '#93c5fd' };
    if (tx._source === 'dev' && tx.type === 'credit') return { label: 'Payment to Developer', icon: '💻', color: '#7c3aed', bg: '#f3e8ff', border: '#c4b5fd' };
    if ((tx._source === 'staff' || tx._source === 'dev') && (tx.type === 'withdrawal' || tx.type === 'debit')) {
      return { label: 'Withdrawal', icon: '💳', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' };
    }
    if (tx._source === 'user' && tx.type === 'credit') {
      if (tx.plan_name && tx.plan_id) return { label: 'Plan Purchase', icon: '💎', color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' };
      return { label: 'Payment Received', icon: '📦', color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' };
    }
    if (tx._source === 'user' && tx.type === 'debit') return { label: 'User Payment', icon: '💳', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' };

    return { label: 'Other', icon: '📋', color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' };
  };

  const getGrantedByAdmin = (tx) => {
    if (!tx.description) return null;
    const match = tx.description.match(/Granted By:\s*([^|]+?)(?:\s*\(|$|\s*\|)/);
    return match ? match[1].trim() : null;
  };
  const getPlanDuration = (tx) => {
    if (!tx.description) return null;
    const match = tx.description.match(/Duration:\s*(\d+)\s*days/);
    return match ? match[1] : null;
  };
  const getAdminNote = (tx) => {
    if (!tx.description) return null;
    const match = tx.description.match(/Note:\s*(.+)$/);
    return match ? match[1].trim() : null;
  };

  // ============================================
  // FETCH DATA
  // ============================================
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const [userRes, staffRes, devRes] = await Promise.all([
        UserTransactionsGet(), StaffTransactionsGet(), DevTransactionsGet(),
      ]);

      const sortByDateDesc = (a, b) => new Date(b.created_date || b.date || 0) - new Date(a.created_date || a.date || 0);

      const deduplicate = (arr, source) => {
        const seen = new Map();
        return arr.map(tx => ({ ...tx, _source: source })).filter(tx => {
          const key = `${tx.id}-${tx.amount}-${tx.UTI || ''}-${tx.ref_id || ''}-${tx.created_date || tx.date || ''}`;
          if (seen.has(key)) return false;
          seen.set(key, true);
          return true;
        });
      };

      setUserTransactions(deduplicate(Array.isArray(userRes) ? userRes : [], "user").filter(tx => tx.amount >= 0 && getTxUserName(tx)).sort(sortByDateDesc));
      setStaffTransactions(deduplicate(Array.isArray(staffRes) ? staffRes : [], "staff").sort(sortByDateDesc));
      setDevTransactions(deduplicate(Array.isArray(devRes) ? devRes : [], "dev").sort(sortByDateDesc));
    } catch (error) {
      message.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTransactions(); }, []);

  const currentData = useMemo(() => {
    if (activeTab === "user") return userTransactions;
    if (activeTab === "staff") return staffTransactions;
    if (activeTab === "dev") return devTransactions;
    const combined = [...userTransactions, ...staffTransactions, ...devTransactions];
    const seen = new Map();
    return combined.filter(tx => {
      const uniqueKey = `${tx.id}-${tx._source}-${tx.amount}-${tx.UTI || ''}-${tx.ref_id || ''}`;
      if (seen.has(uniqueKey)) return false;
      seen.set(uniqueKey, true);
      return true;
    }).sort((a, b) => new Date(b.created_date || b.date || 0) - new Date(a.created_date || a.date || 0));
  }, [activeTab, userTransactions, staffTransactions, devTransactions]);

  const filteredData = useMemo(() => {
    return currentData.filter((tx) => {
      const name = getTxUserName(tx).toLowerCase();
      const contact = getTxContact(tx);
      const ref = generateRefNumber("transaction", tx.id).toLowerCase();

      const matchSearch = !searchText || name.includes(searchText.toLowerCase()) || contact.includes(searchText) || ref.includes(searchText.toLowerCase()) || String(tx.amount).includes(searchText) || (tx.UTI || "").toLowerCase().includes(searchText.toLowerCase()) || (tx.plan_name || "").toLowerCase().includes(searchText.toLowerCase());
      const matchStatus = statusFilter === "all" || tx.status === statusFilter;
      const adminType = getAdminType(tx);
      const matchType = typeFilter === "all" || adminType === typeFilter;

      let matchDate = true;
      if (dateRange && dateRange.length === 2) {
        const txDate = new Date(getTxDate(tx));
        matchDate = txDate >= dateRange[0].toDate() && txDate <= dateRange[1].toDate();
      }

      let matchCategory = true;
      if (categoryFilter !== "all") {
        const cat = getAdminCategory(tx);
        const isStaff = isStaffGrant(tx);
        const uti = String(tx.UTI || '').toUpperCase();
        if (categoryFilter === 'plan_purchase') matchCategory = cat.label === 'Plan Purchase';
        else if (categoryFilter === 'admin_grant') matchCategory = isAdminGrant(tx) && !isStaff;
        else if (categoryFilter === 'staff_grant') matchCategory = isAdminGrant(tx) && isStaff;
        else if (categoryFilter === 'cash_grant') matchCategory = uti.startsWith('CASH-');
        else if (categoryFilter === 'cash_admin') matchCategory = uti.startsWith('CASH-') && !isStaff;
        else if (categoryFilter === 'cash_staff') matchCategory = uti.startsWith('CASH-') && isStaff;
        else if (categoryFilter === 'online_grant') matchCategory = uti.startsWith('ONLINE-');
        else if (categoryFilter === 'online_admin') matchCategory = uti.startsWith('ONLINE-') && !isStaff;
        else if (categoryFilter === 'online_staff') matchCategory = uti.startsWith('ONLINE-') && isStaff;
        else if (categoryFilter === 'upi_grant') matchCategory = uti.startsWith('UPI-');
        else if (categoryFilter === 'upi_admin') matchCategory = uti.startsWith('UPI-') && !isStaff;
        else if (categoryFilter === 'upi_staff') matchCategory = uti.startsWith('UPI-') && isStaff;
        else if (categoryFilter === 'bank_grant') matchCategory = uti.startsWith('BANK-');
        else if (categoryFilter === 'bank_admin') matchCategory = uti.startsWith('BANK-') && !isStaff;
        else if (categoryFilter === 'bank_staff') matchCategory = uti.startsWith('BANK-') && isStaff;
        else if (categoryFilter === 'gift_grant') matchCategory = uti.startsWith('GIFT-');
        else if (categoryFilter === 'payment_received') matchCategory = cat.label === 'Payment Received';
        else if (categoryFilter === 'staff_commission') matchCategory = cat.label === 'Payment to Staff';
        else if (categoryFilter === 'dev_commission') matchCategory = cat.label === 'Payment to Developer';
        else if (categoryFilter === 'visit') matchCategory = cat.label === 'Visit Commission';
        else if (categoryFilter === 'referral') matchCategory = cat.label === 'Referral Bonus';
        else if (categoryFilter === 'payment') matchCategory = cat.label === 'User Payment' || cat.label === 'Withdrawal';
      }
      return matchSearch && matchStatus && matchType && matchDate && matchCategory;
    });
  }, [currentData, searchText, statusFilter, typeFilter, categoryFilter, dateRange]);

  useEffect(() => { setCurrentPage(1); }, [searchText, statusFilter, typeFilter, categoryFilter, dateRange, activeTab]);

  const getRevenueSummary = () => {
    const data = [...userTransactions, ...staffTransactions, ...devTransactions];
    const moneyIn = data.filter(tx => tx._source === 'user' && tx.type === 'credit' && tx.plan_name !== 'User Referral Bonus' && !(tx.ref_id && String(tx.ref_id).startsWith('USER-REFER-'))).reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
    const staffPaid = data.filter(tx => tx._source === 'staff').reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
    const devPaid = data.filter(tx => tx._source === 'dev').reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
    const userReferralPaid = data.filter(tx => tx.plan_name === 'User Referral Bonus' || (tx.ref_id && String(tx.ref_id).startsWith('USER-REFER-'))).reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
    const totalDebit = staffPaid + devPaid + userReferralPaid;
    return { moneyIn, staffPaid, devPaid, userReferralPaid, totalDebit, netRevenue: moneyIn - totalDebit };
  };

  const currentStats = useMemo(() => {
    const totalAmount = filteredData.reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
    const creditAmount = filteredData.filter(t => getAdminType(t) === "credit").reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
    const debitAmount = filteredData.filter(t => getAdminType(t) === "debit").reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
    return {
      total: filteredData.length, totalAmount, creditAmount, debitAmount,
      success: filteredData.filter(t => t.status === "success" || t.status === "approved").length,
      pending: filteredData.filter(t => t.status === "pending").length,
      failed: filteredData.filter(t => t.status === "failed" || t.status === "rejected").length,
    };
  }, [filteredData]);

  const handleViewDetails = (tx) => { setSelectedTx(tx); setDetailsDrawerOpen(true); };
  const clearFilters = () => { setSearchText(""); setStatusFilter("all"); setTypeFilter("all"); setCategoryFilter("all"); setDateRange(null); setCurrentPage(1); };

  const revenue = getRevenueSummary();
  const allTx = [...userTransactions, ...staffTransactions, ...devTransactions];

  // ============================================
  // ORIGINAL TABLE COLUMNS (Restored)
  // ============================================
  const columns = [
    {
      title: "#", key: "sno", width: 50, fixed: "left",
      render: (_, __, i) => (
        <span className="admin-transactions-sno-cell">
          {(currentPage - 1) * pageSize + i + 1}
        </span>
      ),
    },
    {
      title: "Ref No", key: "tx_ref", width: 120,
      render: (_, r) => (
        <span className="admin-transactions-ref-cell">
          {generateRefNumber("transaction", r.id)}
        </span>
      ),
    },
    {
      title: "User/Recipient", key: "user", width: 180,
      render: (_, r) => {
        const name = getTxUserName(r);
        return (
          <div className="admin-transactions-user-cell">
            <div className="admin-transactions-user-avatar-tx">
              {name[0]?.toUpperCase() || "U"}
            </div>
            <div className="admin-transactions-user-info-tx">
              <div className="admin-transactions-user-name-tx">
                {name}
                <Tag style={{ marginLeft: 4, fontSize: 10 }}
                  color={r._source === "user" ? "blue" : r._source === "staff" ? "green" : "purple"}>
                  {r._source}
                </Tag>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Type", key: "type", width: 110,
      render: (_, r) => {
        const adminType = getAdminType(r);
        return (
          <Tag className={`admin-transactions-type-tag admin-transactions-type-${adminType}`}>
            {adminType === "credit" ? <RiseOutlined /> : <FallOutlined />}
            {" "}{adminType === "credit" ? "Credit" : "Debit"}
          </Tag>
        );
      },
    },
    {
      title: "Category", key: "category", width: 200,
      render: (_, r) => {
        const cat = getAdminCategory(r);
        return (
          <Tag style={{ background: cat.bg, color: cat.color, border: `1px solid ${cat.border}`, fontSize: 11, fontWeight: 600 }}>
            {cat.icon} {cat.label}
          </Tag>
        );
      },
    },
    {
      title: "Amount", dataIndex: "amount", key: "amount", width: 130,
      render: (amount, r) => {
        const adminType = getAdminType(r);
        const amt = parseFloat(amount || 0);
        return (
          <span style={{ fontWeight: 700, fontSize: 14, color: amt === 0 ? '#94a3b8' : (adminType === "credit" ? '#52c41a' : '#ef4444') }}>
            {amt === 0 ? '' : (adminType === "credit" ? "+" : "-")}
            ₹{amt.toLocaleString("en-IN")}
            {amt === 0 && <span style={{ fontSize: 10, marginLeft: 4, color: '#ec4899' }}>FREE</span>}
          </span>
        );
      },
    },
    {
      title: "Payment Mode", key: "payment_mode_display", width: 160,
      render: (_, r) => {
        if (isAdminGrant(r)) {
          const pm = getPaymentModeFromUTI(r.UTI);
          const isStaff = isStaffGrant(r);
          if (pm) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Tag color={pm.color} style={{ fontWeight: 700, fontSize: 11, margin: 0 }}>
                  {pm.icon} {pm.label}
                </Tag>
                <Tag color={isStaff ? 'green' : 'purple'} style={{ fontWeight: 700, fontSize: 10, margin: 0 }}>
                  {isStaff ? '👥 Staff' : '👑 Admin'}
                </Tag>
              </div>
            );
          }
          return (
            <Tag color={isStaff ? 'green' : 'purple'} style={{ fontWeight: 700 }}>
              {isStaff ? '👥 STAFF GRANT' : '👑 ADMIN GRANT'}
            </Tag>
          );
        }
        if (r.UTI && r.UTI !== 'UPI Transaction Id' && !r.UTI.startsWith('GRANT-')) {
          return <Tag color="blue" style={{ fontWeight: 700 }}>🌐 ONLINE</Tag>;
        }
        return <Tag color="default">-</Tag>;
      },
    },
    {
      title: "Plan / Reason", key: "plan_info", width: 160,
      render: (_, r) => {
        if (r.plan_name) {
          const duration = getPlanDuration(r);
          return (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#ff0055' }}>{r.plan_name}</div>
              {duration && <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>📅 {duration} days</div>}
            </div>
          );
        }
        return <span style={{ color: '#94a3b8' }}>-</span>;
      },
    },
    {
      title: "UTI / Ref", key: "payment_mode", width: 160,
      render: (_, r) => (
        <span className="admin-transactions-payment-mode-cell" style={{ fontSize: 11, fontFamily: 'monospace' }}>
          <CreditCardOutlined />
          {" "}{r.UTI || r.upi_id || "N/A"}
        </span>
      ),
    },
    {
      title: "Status", dataIndex: "status", key: "status", width: 100,
      render: (status) => {
        const isSuccess = status === "success" || status === "approved";
        const isPending = status === "pending";
        return (
          <Tag className={`admin-transactions-status-tag admin-transactions-status-${isSuccess ? "success" : isPending ? "pending" : "failed"}`}>
            {isSuccess ? <CheckCircleOutlined /> : isPending ? <ClockCircleOutlined /> : <CloseCircleOutlined />}
            {" "}{capitalize(status) || "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "Date", key: "date", width: 110,
      render: (_, r) => (
        <span className="admin-transactions-date-cell">
          {formatDate(getTxDate(r))}
        </span>
      ),
    },
    {
      title: "Action", key: "action", width: 70, fixed: "right",
      render: (_, r) => (
        <Tooltip title="View Details">
          <Button
            size="small"
            icon={<EyeOutlined />}
            className="admin-transactions-action-btn admin-transactions-action-view"
            onClick={() => handleViewDetails(r)}
          />
        </Tooltip>
      ),
    },
  ];

  const handleExportCSV = () => exportToCSV(filteredData, [{ key: "tx_ref", label: "Ref" }, { key: "amount", label: "Amount" }], "transactions");
  const handleExportPDF = () => exportToPDF(filteredData, [{ key: "tx_ref", label: "Ref" }, { key: "amount", label: "Amount" }], { title: "Transactions", filename: "transactions" });

  const tabItems = [
    { key: "all", label: (<span className="admin-transactions-tab-label"><TransactionOutlined /> All <span className="admin-transactions-tab-count">{allTx.length}</span></span>) },
    { key: "user", label: (<span className="admin-transactions-tab-label"><UserOutlined /> Users <span className="admin-transactions-tab-count">{userTransactions.length}</span></span>) },
    { key: "staff", label: (<span className="admin-transactions-tab-label"><IdcardOutlined /> Staff <span className="admin-transactions-tab-count">{staffTransactions.length}</span></span>) },
    { key: "dev", label: (<span className="admin-transactions-tab-label"><BankOutlined /> Developer <span className="admin-transactions-tab-count">{devTransactions.length}</span></span>) },
  ];

  return (
    <div className="admin-transactions-page">

      {/* ================= HEADER & STATS TOGGLE ================= */}
      <div className="admin-transactions-header-stats-wrapper">
        <div className="admin-transactions-page-header">
          <div className="admin-transactions-header-left">
            <div className="admin-transactions-title-row">
              <div className="admin-transactions-title-icon"><BankOutlined /></div>
              <div className="admin-transactions-title-content">
                <h2 className="admin-transactions-page-title">Admin Bank Transactions</h2>
                <p className="admin-transactions-page-subtitle">
                  {currentStats.total} transactions • Credit: +₹{currentStats.creditAmount.toLocaleString("en-IN")} • Debit: -₹{currentStats.debitAmount.toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          </div>

          <div className="admin-transactions-header-actions">
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchTransactions} loading={loading} className="admin-transactions-icon-btn icon-mobile-btn" />
            </Tooltip>
            <Dropdown menu={{ items: [{ key: "1", label: `Export CSV`, onClick: () => handleExportCSV() }] }}>
              <Button icon={<FileExcelOutlined />} className="admin-transactions-csv-btn icon-mobile-btn">
                <span className="hide-on-mobile">CSV <DownOutlined style={{ fontSize: 10 }} /></span>
              </Button>
            </Dropdown>
            <Dropdown menu={{ items: [{ key: "1", label: `Export PDF`, onClick: () => handleExportPDF() }] }}>
              <Button icon={<FilePdfOutlined />} className="admin-transactions-pdf-btn icon-mobile-btn">
                <span className="hide-on-mobile">PDF <DownOutlined style={{ fontSize: 10 }} /></span>
              </Button>
            </Dropdown>
            {isMobile && (
              <Button
                className="toggle-stats-btn icon-mobile-btn"
                onClick={() => setShowStats(!showStats)}
                icon={showStats ? <UpOutlined /> : <DownOutlined />}
              />
            )}
          </div>
        </div>

        {/* TOGGLEABLE STATS GRID */}
        <div
          className={[
            "stats-grid-wrapper",
            isMobile ? (showStats ? "is-open" : "is-closed") : "is-desktop"
          ].join(" ")}
        >  <div className="stat-grid-item">
            <TransactionOutlined style={{ color: '#666' }} />
            <div className="stat-grid-content"><b>{currentStats.total}</b><span>TOTAL</span></div>
          </div>
          <div className="stat-grid-item">
            <RiseOutlined style={{ color: '#52c41a' }} />
            <div className="stat-grid-content"><b>+₹{currentStats.creditAmount}</b><span>CREDIT</span></div>
          </div>
          <div className="stat-grid-item">
            <FallOutlined style={{ color: '#ff4d4f' }} />
            <div className="stat-grid-content"><b>-₹{currentStats.debitAmount}</b><span>DEBIT</span></div>
          </div>
          <div className="stat-grid-item">
            <DollarOutlined style={{ color: '#722ed1' }} />
            <div className="stat-grid-content"><b>₹{currentStats.creditAmount - currentStats.debitAmount}</b><span>PROFIT</span></div>
          </div>
          <div className="stat-grid-item">
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <div className="stat-grid-content"><b>{currentStats.success}</b><span>SUCCESS</span></div>
          </div>
          <div className="stat-grid-item">
            <ClockCircleOutlined style={{ color: '#faad14' }} />
            <div className="stat-grid-content"><b>{currentStats.pending}</b><span>PENDING</span></div>
          </div>
        </div>
      </div>

      {/* ================= ADMIN BANK SUMMARY ================= */}
      <div className="admin-bank-summary-card">
        <div className="summary-header">
          <BankOutlined style={{ color: '#ff0055' }} /> <h3>Admin Bank Summary</h3>
        </div>
        <div className="summary-grid">
          <div className="summary-box credit">
            <div className="box-icon"><RiseOutlined /></div>
            <div className="box-text"><span>TOTAL CREDIT</span><strong className="pos">+₹{currentStats.creditAmount.toLocaleString("en-IN")}</strong></div>
          </div>
          <div className="summary-box debit">
            <div className="box-icon"><FallOutlined /></div>
            <div className="box-text"><span>TOTAL DEBIT</span><strong className="neg">-₹{currentStats.debitAmount.toLocaleString("en-IN")}</strong></div>
          </div>
          <div className="summary-box staff">
            <div className="box-icon"><IdcardOutlined /></div>
            <div className="box-text"><span>PAID TO STAFF</span><strong className="neg">-₹{revenue.staffPaid.toLocaleString("en-IN")}</strong></div>
          </div>
          <div className="summary-box dev">
            <div className="box-icon"><BankOutlined /></div>
            <div className="box-text"><span>PAID TO DEV</span><strong className="neg">-₹{revenue.devPaid.toLocaleString("en-IN")}</strong></div>
          </div>
          <div className="summary-box profit">
            <div className="box-icon"><DollarOutlined /></div>
            <div className="box-text"><span>NET PROFIT</span><strong className={revenue.netRevenue >= 0 ? "pos" : "neg"}>{revenue.netRevenue >= 0 ? '+' : ''}₹{revenue.netRevenue.toLocaleString("en-IN")}</strong></div>
          </div>
        </div>
      </div>

      {/* ================= FILTERS (Mobile responsive 50-50) ================= */}
      <div className="app-filters-card">
        <div className="filter-grid">
          {/* Row 1: Search */}
          <div className="filter-col full-width">
            <label>🔍 SEARCH</label>
            <Input placeholder="Search name, mobile, UTI..." prefix={<SearchOutlined />} value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear className="app-input" />
          </div>

          {/* Row 2: Type & Category */}
          <div className="filter-row-split">
            <div className="filter-col half-width">
              <label>💳 TYPE</label>
              <Select value={typeFilter} onChange={setTypeFilter} className="app-select" suffixIcon={<DownOutlined />}>
                <Option value="all">All Types</Option>
                <Option value="credit">Credit</Option>
                <Option value="debit">Debit</Option>
              </Select>
            </div>
            <div className="filter-col half-width">
              <label>📦 CATEGORY</label>
              <Select value={categoryFilter} onChange={setCategoryFilter} className="app-select" suffixIcon={<DownOutlined />}>
                <Option value="all">All Categories</Option>
                <Option value="plan_purchase">Plan Purchase</Option>
                <Option value="admin_grant">Admin Grant</Option>
                <Option value="staff_grant">Staff Grant</Option>
              </Select>
            </div>
          </div>

          {/* Row 3: Date & Status */}
          <div className="filter-row-split">
            <div className="filter-col half-width">
              <label>📅 DATE</label>
              <RangePicker value={dateRange} onChange={setDateRange} className="app-datepicker" />
            </div>
            <div className="filter-col half-width">
              <label>📊 STATUS</label>
              <Select value={statusFilter} onChange={setStatusFilter} className="app-select" suffixIcon={<DownOutlined />}>
                <Option value="all">All Status</Option>
                <Option value="success">Success</Option>
                <Option value="pending">Pending</Option>
                <Option value="failed">Failed</Option>
              </Select>
            </div>
          </div>

          {/* Row 4: Clear */}
          <div className="filter-col full-width" style={{ marginTop: 8 }}>
            <Button className="app-clear-btn" onClick={clearFilters} icon={<ReloadOutlined />}>Clear All</Button>
          </div>
        </div>
      </div>

      {/* ================= DATA RENDER (Always Table) ================= */}
      <div className="admin-transactions-table-card">
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} size="small" className="admin-transactions-tx-tabs" />

        <Table rowKey="id" columns={columns} dataSource={filteredData} loading={loading} size="small" pagination={{ current: currentPage, pageSize: pageSize, onChange: (p, s) => { setCurrentPage(p); setPageSize(s); } }} scroll={{ x: 1300 }} className="admin-transactions-table" />
      </div>

      {/* ================= 🎨 PREMIUM DETAILS DRAWER ================= */}
      <Drawer
        title={
          <div className="tx-drawer-title">
            <div className="tx-drawer-title-icon"><BankOutlined /></div>
            <div>
              <h3>Transaction Details</h3>
              <p>Complete transaction info</p>
            </div>
          </div>
        }
        placement="right"
        width={isMobile ? '100%' : 500}
        onClose={() => setDetailsDrawerOpen(false)}
        open={detailsDrawerOpen}
        className="tx-custom-drawer"
      >
        {selectedTx && (() => {
          const adminType = getAdminType(selectedTx);
          const adminCat = getAdminCategory(selectedTx);
          const isGrant = isAdminGrant(selectedTx);
          const isStaff = isStaffGrant(selectedTx);
          const paymentMode = isGrant ? getPaymentModeFromUTI(selectedTx.UTI) : null;
          const grantedByAdmin = getGrantedByAdmin(selectedTx);
          const planDuration = getPlanDuration(selectedTx);
          const adminNote = getAdminNote(selectedTx);
          const amt = parseFloat(selectedTx.amount || 0);
          const isSuccess = selectedTx.status === "success" || selectedTx.status === "approved";
          const isPending = selectedTx.status === "pending";
          const statusClass = isSuccess ? "success" : isPending ? "pending" : "failed";

          return (
            <div className="tx-drawer-content">
              {/* Amount Hero Card */}
              <div className={`tx-amount-hero tx-hero-${adminType} ${amt === 0 ? 'tx-hero-free' : ''}`}>
                <div className="tx-amount-icon">
                  {isGrant ? <CrownOutlined /> : (adminType === "credit" ? <RiseOutlined /> : <FallOutlined />)}
                </div>
                <div className="tx-amount-value">
                  {amt === 0 ? 'FREE' : `${adminType === "credit" ? "+" : "-"}₹${amt.toLocaleString("en-IN")}`}
                </div>
                <div className="tx-amount-ref">
                  {generateRefNumber("transaction", selectedTx.id)}
                </div>
                <div className="tx-amount-badges">
                  <Tag className={`admin-transactions-type-tag admin-transactions-type-${adminType}`}>
                    {adminType === "credit" ? <RiseOutlined /> : <FallOutlined />} {adminType === "credit" ? "Credit" : "Debit"}
                  </Tag>
                  <Tag className={`admin-transactions-status-tag admin-transactions-status-${statusClass}`}>
                    {isSuccess ? <CheckCircleOutlined /> : isPending ? <ClockCircleOutlined /> : <CloseCircleOutlined />}
                    {" "}{capitalize(selectedTx.status)}
                  </Tag>
                  {isGrant && (
                    <Tag color={isStaff ? 'green' : 'purple'} style={{ fontWeight: 700, borderRadius: 12 }}>
                      {isStaff ? '👥 Staff Grant' : '👑 Admin Grant'}
                    </Tag>
                  )}
                </div>
              </div>

              {/* Grant Specific Info */}
              {isGrant && (
                <div className={`tx-grant-box ${isStaff ? 'grant-staff' : 'grant-admin'}`}>
                  <div className="tx-grant-header">
                    {isStaff ? <IdcardOutlined /> : <CrownOutlined />}
                    <span>{isStaff ? 'Staff Grant Details' : 'Admin Grant Details'}</span>
                  </div>
                  <div className="tx-grant-body">
                    {paymentMode && (
                      <div className="tx-grant-row">
                        <span className="tx-grant-label">Payment Mode</span>
                        <Tag color={paymentMode.color} style={{ fontWeight: 700 }}>
                          {paymentMode.icon} {paymentMode.full}
                        </Tag>
                      </div>
                    )}
                    {grantedByAdmin && (
                      <div className="tx-grant-row">
                        <span className="tx-grant-label">Granted By</span>
                        <span className="tx-grant-value">
                          {isStaff ? '👥' : '👑'} {grantedByAdmin}
                        </span>
                      </div>
                    )}
                    {selectedTx.plan_name && (
                      <div className="tx-grant-row">
                        <span className="tx-grant-label">Plan</span>
                        <span className="tx-grant-value" style={{ color: '#ff0055' }}>
                          💎 {selectedTx.plan_name}
                        </span>
                      </div>
                    )}
                    {planDuration && (
                      <div className="tx-grant-row">
                        <span className="tx-grant-label">Duration</span>
                        <span className="tx-grant-value" style={{ color: '#0891b2' }}>
                          📅 {planDuration} days
                        </span>
                      </div>
                    )}
                    {adminNote && (
                      <div className="tx-note-box">
                        <div className="tx-note-label">
                          📝 {isStaff ? 'STAFF NOTE' : 'ADMIN NOTE'}
                        </div>
                        <div className="tx-note-value">"{adminNote}"</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Transaction Info Section */}
              <div className="tx-detail-section">
                <h4><FileTextOutlined /> Transaction Info</h4>
                <div className="tx-detail-item">
                  <div className="tx-detail-icon-wrap"><IdcardOutlined /></div>
                  <div className="tx-detail-label">Ref No</div>
                  <div className="tx-detail-value">
                    <span className="tx-mono-tag">{generateRefNumber("transaction", selectedTx.id)}</span>
                  </div>
                </div>

                <div className="tx-detail-item">
                  <div className="tx-detail-icon-wrap"><IdcardOutlined /></div>
                  <div className="tx-detail-label">Category</div>
                  <div className="tx-detail-value">
                    <Tag style={{ background: adminCat.bg, color: adminCat.color, border: `1px solid ${adminCat.border}`, fontWeight: 600 }}>
                      {adminCat.icon} {adminCat.label}
                    </Tag>
                  </div>
                </div>

                {selectedTx.plan_name && !isGrant && (
                  <div className="tx-detail-item">
                    <div className="tx-detail-icon-wrap"><FileTextOutlined /></div>
                    <div className="tx-detail-label">Plan</div>
                    <div className="tx-detail-value" style={{ fontWeight: 700, color: '#ff0055' }}>
                      💎 {selectedTx.plan_name}
                    </div>
                  </div>
                )}

                <div className="tx-detail-item">
                  <div className="tx-detail-icon-wrap"><IdcardOutlined /></div>
                  <div className="tx-detail-label">UTI / Txn ID</div>
                  <div className="tx-detail-value">
                    <span className="tx-mono-tag" style={{ wordBreak: 'break-all' }}>
                      {selectedTx.UTI || "N/A"}
                    </span>
                  </div>
                </div>

                {selectedTx.description && (
                  <div className="tx-detail-item">
                    <div className="tx-detail-icon-wrap"><FileTextOutlined /></div>
                    <div className="tx-detail-label">Description</div>
                    <div className="tx-detail-value" style={{ fontSize: 12, color: '#555' }}>
                      {selectedTx.description}
                    </div>
                  </div>
                )}

                {selectedTx.ref_id && (
                  <div className="tx-detail-item">
                    <div className="tx-detail-icon-wrap"><IdcardOutlined /></div>
                    <div className="tx-detail-label">Reference ID</div>
                    <div className="tx-detail-value">
                      <span className="tx-mono-tag" style={{ wordBreak: 'break-all', fontSize: 11 }}>
                        {selectedTx.ref_id}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Recipient / Payer Info */}
              <div className="tx-detail-section">
                <h4><UserOutlined /> {isGrant ? "Recipient" : (adminType === "credit" ? "Payer" : "Recipient")} Info</h4>
                <div className="tx-detail-item">
                  <div className="tx-detail-icon-wrap"><UserOutlined /></div>
                  <div className="tx-detail-label">Name</div>
                  <div className="tx-detail-value">{getTxUserName(selectedTx)}</div>
                </div>
                <div className="tx-detail-item">
                  <div className="tx-detail-icon-wrap"><IdcardOutlined /></div>
                  <div className="tx-detail-label">Source</div>
                  <div className="tx-detail-value">
                    <Tag color={selectedTx._source === "user" ? "blue" : selectedTx._source === "staff" ? "green" : "purple"}>
                      {selectedTx._source?.toUpperCase() || "N/A"}
                    </Tag>
                  </div>
                </div>
                {selectedTx.contact && (
                  <div className="tx-detail-item">
                    <div className="tx-detail-icon-wrap"><CreditCardOutlined /></div>
                    <div className="tx-detail-label">Contact</div>
                    <div className="tx-detail-value">{selectedTx.contact}</div>
                  </div>
                )}
                {selectedTx.upi_id && (
                  <div className="tx-detail-item">
                    <div className="tx-detail-icon-wrap"><CreditCardOutlined /></div>
                    <div className="tx-detail-label">UPI ID</div>
                    <div className="tx-detail-value">
                      <span className="tx-mono-tag">{selectedTx.upi_id}</span>
                    </div>
                  </div>
                )}
                {selectedTx.bank_account && (
                  <div className="tx-detail-item">
                    <div className="tx-detail-icon-wrap"><BankOutlined /></div>
                    <div className="tx-detail-label">Bank Acc</div>
                    <div className="tx-detail-value">
                      <span className="tx-mono-tag">{selectedTx.bank_account}</span>
                    </div>
                  </div>
                )}
                {selectedTx.ifsc_code && (
                  <div className="tx-detail-item">
                    <div className="tx-detail-icon-wrap"><IdcardOutlined /></div>
                    <div className="tx-detail-label">IFSC</div>
                    <div className="tx-detail-value">
                      <span className="tx-mono-tag">{selectedTx.ifsc_code}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Date Info */}
              <div className="tx-detail-section">
                <h4><CalendarOutlined /> Timing</h4>
                <div className="tx-detail-item">
                  <div className="tx-detail-icon-wrap"><CalendarOutlined /></div>
                  <div className="tx-detail-label">Date</div>
                  <div className="tx-detail-value">{formatDateTime(getTxDate(selectedTx))}</div>
                </div>
              </div>
            </div>
          );
        })()}
      </Drawer>
    </div>
  );
}

export default Transactions;